import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  BookOpen,
  Building2,
  ChevronRight,
  ExternalLink,
  FileText,
  FileType2,
  FileUp,
  Globe,
  HelpCircle,
  Library,
  Link2,
  Pencil,
  Scale,
  ScanSearch,
  Search,
  ShieldCheck,
  Sparkles,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import { Button, Card, IconTile, Spinner } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { adminSetDepartment } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAdmin } from '@/hooks/useAdmin';
import {
  DEPARTMENTS,
  UNCATEGORIZED,
  displayName,
  groupByDepartment,
  groupSources,
  hostOf,
  indexedDateShort,
  indexedTooltip,
  isInternalRow,
  isLawDomain,
  type SourceRow,
} from '@/lib/kbSources';
import { AddSourceModal } from '@/components/features/ai/AddSourceModal';
import { AddFileModal } from '@/components/features/kb/AddFileModal';
import { CrawlSiteModal } from '@/components/features/kb/CrawlSiteModal';
import { EditSourceModal } from '@/components/features/kb/EditSourceModal';
import { DeleteSourceDialog } from '@/components/features/kb/DeleteSourceDialog';

export function KnowledgeBasePage() {
  const { isAdmin } = useAdmin();
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [addUrlOpen, setAddUrlOpen] = useState(false);
  const [addFileOpen, setAddFileOpen] = useState(false);
  const [crawlOpen, setCrawlOpen] = useState(false);
  const [editSource, setEditSource] = useState<SourceRow | null>(null);
  const [deleteSource, setDeleteSource] = useState<SourceRow | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [savingDept, setSavingDept] = useState<Set<string>>(new Set());
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    supabase.rpc('list_kb_sources').then(({ data, error }) => {
      if (!mounted) return;
      if (!error && data) setSources(data as SourceRow[]);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? sources.filter((s) => s.filename.toLowerCase().includes(q)) : sources;
    return groupSources(filtered);
  }, [sources, query]);

  const totalChunks = sources.reduce((n, s) => n + s.chunk_count, 0);
  const emptySources = sources.filter((s) => s.chunk_count === 0);
  const webSourcesAll = sources.filter((s) => !isInternalRow(s));
  const domainCountAll = new Set(webSourcesAll.map((s) => hostOf(s.filename))).size;
  const internalCountAll = sources.length - webSourcesAll.length;
  const filteredCount =
    grouped.internal.length + grouped.domains.reduce((n, d) => n + d.items.length, 0);

  const handleSetDepartment = async (src: SourceRow, department: string | null) => {
    const prev = src.department ?? null;
    if (prev === department) return;
    // Optimistisk uppdatering — slår tillbaka vid fel.
    setSources((list) => list.map((s) => (s.filename === src.filename ? { ...s, department } : s)));
    setSavingDept((s) => new Set(s).add(src.filename));
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Inte inloggad');
      await adminSetDepartment(src.filename, department, token);
    } catch {
      setSources((list) => list.map((s) => (s.filename === src.filename ? { ...s, department: prev } : s)));
    } finally {
      setSavingDept((s) => {
        const next = new Set(s);
        next.delete(src.filename);
        return next;
      });
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-5 lg:p-6">
      <div className="max-w-[1400px] mx-auto pb-10 space-y-4">
        <header className="flex items-center justify-between gap-4">
          <p className="text-[12.5px] text-ink-500">
            Alla källor ELvis söker i · lägg till nya URL:er eller textfiler direkt här
          </p>
          <div className="hidden sm:flex items-center gap-3 shrink-0">
            {isAdmin && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-ink-900 text-surface text-[10px] font-semibold uppercase tracking-[0.06em]">
                <ShieldCheck size={12} strokeWidth={2} />
                Admin
              </span>
            )}
            <div className="flex items-center gap-1.5 text-[11px] text-ink-500">
              <Sparkles size={12} strokeWidth={2} />
              <span>Semantisk indexering</span>
            </div>
          </div>
        </header>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatPill label="Totalt källor" value={sources.length} />
          <StatPill label="Chunks indexerade" value={totalChunks} />
          <StatPill label="Webbplatser" value={domainCountAll} />
          <StatPill label="Interna dokument" value={internalCountAll} />
        </div>

        {/* En källa utan chunks ser i listan ut precis som en fungerande källa.
            Sju sådana stod obemärkta i två månader och gjorde att ELvis inte
            kunde svara på de vanligaste frågorna. Nu syns det direkt. */}
        {emptySources.length > 0 && (
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <IconTile icon={<TriangleAlert size={14} strokeWidth={2} />} tone="warning" size="sm" />
              <div className="flex-1 min-w-0">
                <h2 className="text-[13.5px] font-semibold text-amber-900 leading-tight">
                  {emptySources.length === 1
                    ? '1 källa saknar innehåll'
                    : `${emptySources.length} källor saknar innehåll`}
                </h2>
                <p className="text-[12px] text-amber-800 leading-relaxed mt-1">
                  De står med i listan men är inte indexerade, så ELvis kan inte söka i dem och
                  svarar som om de inte fanns.{' '}
                  {/* Åtgärden skiljer sig åt: en webbadress hämtas om, en fil måste laddas upp igen. */}
                  {emptySources.some((s) => !isInternalRow(s)) &&
                    'Webbadresser fyller du genom att lägga till dem på nytt så att sidan hämtas om. '}
                  {emptySources.some((s) => isInternalRow(s)) &&
                    'Uppladdade dokument fyller du genom att ladda upp filen igen. '}
                  Behövs källan inte kan du ta bort den.
                </p>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {emptySources.map((s) => (
                    <li
                      key={s.filename}
                      className="px-2 py-0.5 rounded bg-surface border border-amber-200 text-[11.5px] text-amber-900 max-w-full truncate"
                      title={s.filename}
                    >
                      {displayName(s)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        )}

        {/* Two-column: sources list + instructions */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-5">
          {/* Sources list */}
          <Card variant="glass" className="p-6 flex flex-col min-h-[520px]">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <div className="flex-1">
                <h2 className="font-bold text-ink-900 text-[15px] leading-none">Alla källor</h2>
                <p className="text-[11px] font-semibold text-ink-500 mt-1">
                  {loading
                    ? 'Laddar…'
                    : query
                      ? `${filteredCount} träffar för "${query}"`
                      : `${sources.length} källor · grupperade efter typ`}
                </p>
              </div>
              <div className="flex gap-2">
                {isAdmin && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<ScanSearch size={14} strokeWidth={1.75} />}
                    onClick={() => setCrawlOpen(true)}
                  >
                    Crawla sajt
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Link2 size={14} strokeWidth={1.75} />}
                  onClick={() => setAddUrlOpen(true)}
                >
                  Lägg till URL
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FileUp size={14} strokeWidth={1.75} />}
                  onClick={() => setAddFileOpen(true)}
                >
                  Ladda upp fil
                </Button>
              </div>
            </div>

            <div className="relative mb-4">
              <Search
                size={14}
                strokeWidth={1.75}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-500"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Sök bland alla källor…"
                className="w-full h-10 pl-9 pr-8 rounded-xl bg-surface border border-ink-200 text-[13px] font-medium text-ink-800 placeholder:text-ink-500 focus:border-ink-900 focus:ring-2 focus:ring-ink-100 focus:outline-none transition-all"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-700 p-0.5"
                  aria-label="Rensa sökning"
                >
                  <X size={14} strokeWidth={2} />
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-10">
                <Spinner size={22} className="text-brand-500" />
              </div>
            ) : sources.length === 0 ? (
              <EmptySources onAddUrl={() => setAddUrlOpen(true)} onAddFile={() => setAddFileOpen(true)} />
            ) : filteredCount === 0 ? (
              <p className="text-[13px] text-ink-500 text-center py-10 font-medium">
                Inga källor matchar "{query}"
              </p>
            ) : (
              <div className="flex-1 space-y-2.5 -mx-1 px-1 overflow-y-auto">
                {grouped.domains.map((d) => (
                  <Group
                    key={d.host}
                    label={d.label}
                    icon={
                      isLawDomain(d.host) ? (
                        <Scale size={13} strokeWidth={1.75} />
                      ) : (
                        <Globe size={13} strokeWidth={1.75} />
                      )
                    }
                    items={d.items}
                    open={!!query || openGroups.has(d.host)}
                    onToggle={() => toggleGroup(d.host)}
                    isAdmin={isAdmin}
                    onEdit={setEditSource}
                    onDelete={setDeleteSource}
                  />
                ))}
                <InternalGroup
                  items={grouped.internal}
                  open={!!query || openGroups.has('internal')}
                  onToggle={() => toggleGroup('internal')}
                  isAdmin={isAdmin}
                  onEdit={setEditSource}
                  onDelete={setDeleteSource}
                  onSetDepartment={handleSetDepartment}
                  savingDept={savingDept}
                />
              </div>
            )}
          </Card>

          {/* Instructions */}
          <InstructionsPanel />
        </div>

        <AddSourceModal
          open={addUrlOpen}
          onClose={() => setAddUrlOpen(false)}
          onAdded={() => setRefreshKey((k) => k + 1)}
        />
        <AddFileModal
          open={addFileOpen}
          onClose={() => setAddFileOpen(false)}
          onAdded={() => setRefreshKey((k) => k + 1)}
        />
        <CrawlSiteModal
          open={crawlOpen}
          onClose={() => setCrawlOpen(false)}
          onAdded={() => setRefreshKey((k) => k + 1)}
        />
        <EditSourceModal
          open={editSource !== null}
          filename={editSource?.filename ?? null}
          onClose={() => setEditSource(null)}
          onSaved={() => setRefreshKey((k) => k + 1)}
        />
        <DeleteSourceDialog
          open={deleteSource !== null}
          filename={deleteSource?.filename ?? null}
          chunkCount={deleteSource?.chunk_count ?? 0}
          onClose={() => setDeleteSource(null)}
          onDeleted={() => setRefreshKey((k) => k + 1)}
        />
      </div>
    </div>
  );
}

/**
 * Datumet då källans innehåll senast lades in i kunskapsbasen.
 *
 * Efterfrågat av El-kretsen: man ska kunna se vilken version av t.ex.
 * prislistan som faktiskt ligger inne, utan att öppna dokumentet. Kort text i
 * raden, hela meningen som hovringstext — samma komponent i båda listvyerna så
 * att de inte kan glida isär.
 */
function IndexedDate({ src }: { src: SourceRow }) {
  const tooltip = indexedTooltip(src);
  if (!src.last_indexed_at || !tooltip) return null;
  return (
    <span
      className="shrink-0 text-[10px] font-medium text-ink-500 tabular-nums"
      title={tooltip}
    >
      {indexedDateShort(src.last_indexed_at)}
    </span>
  );
}

function StatPill({ label, value }: { label: string; value: number }) {
  return (
    <Card variant="glass" className="p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">{label}</p>
      <p className="text-display text-[22px] text-ink-900 tabular-nums leading-none mt-2">
        {value.toLocaleString('sv-SE')}
      </p>
    </Card>
  );
}

function Group({
  label,
  icon,
  items,
  open,
  onToggle,
  isAdmin,
  onEdit,
  onDelete,
}: {
  label: string;
  icon: React.ReactNode;
  items: SourceRow[];
  open: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  onEdit: (src: SourceRow) => void;
  onDelete: (src: SourceRow) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border border-ink-100 bg-surface overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-ink-50 transition-colors"
      >
        <ChevronRight
          size={14}
          strokeWidth={2}
          className={cn('text-ink-500 transition-transform duration-200', open && 'rotate-90')}
        />
        <span className="text-ink-500">{icon}</span>
        <span className="flex-1 text-left text-[13px] font-bold text-ink-800">{label}</span>
        <span className="text-[11px] font-bold text-ink-500 tabular-nums">{items.length}</span>
      </button>
      {open && (
        <ul className="border-t border-ink-100 divide-y divide-ink-100">
          {items.map((src) => {
            const isUrl = !isInternalRow(src);
            const isEditable = !isUrl;
            const href = isUrl ? `https://${src.filename}` : null;
            return (
              <li key={src.filename}>
                <div className="group/row flex items-center gap-3 px-4 py-2.5 hover:bg-ink-50 transition-colors">
                  {isUrl ? (
                    <Link2 size={12} strokeWidth={1.75} className="text-ink-500 shrink-0" />
                  ) : (
                    <FileText size={12} strokeWidth={1.75} className="text-ink-500 shrink-0" />
                  )}
                  <span
                    className="flex-1 min-w-0 text-[12.5px] font-semibold text-ink-700 truncate"
                    title={src.filename}
                  >
                    {displayName(src)}
                  </span>
                  <IndexedDate src={src} />
                  {/* En källa utan chunks går inte att söka i. Utan den här
                      markeringen ser den likadan ut som alla andra i listan. */}
                  {src.chunk_count === 0 ? (
                    <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[10px] font-semibold uppercase tracking-[0.06em] text-amber-700">
                      <TriangleAlert size={10} strokeWidth={2} />
                      Tom
                    </span>
                  ) : (
                    <span className="shrink-0 text-[10px] font-semibold text-ink-500 tabular-nums">
                      {src.chunk_count} chunks
                    </span>
                  )}
                  {href && (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ink-500 hover:text-ink-900 transition-colors shrink-0"
                      aria-label="Öppna källan"
                    >
                      <ExternalLink size={11} strokeWidth={1.75} />
                    </a>
                  )}
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover/row:opacity-100 transition-opacity">
                      {isEditable && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(src);
                          }}
                          className="p-1.5 rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 transition-colors"
                          title="Redigera innehåll"
                          aria-label={`Redigera ${src.filename}`}
                        >
                          <Pencil size={12} strokeWidth={1.75} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(src);
                        }}
                        className="p-1.5 rounded-md text-ink-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                        title="Ta bort källa"
                        aria-label={`Ta bort ${src.filename}`}
                      >
                        <Trash2 size={12} strokeWidth={1.75} />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function InternalGroup({
  items,
  open,
  onToggle,
  isAdmin,
  onEdit,
  onDelete,
  onSetDepartment,
  savingDept,
}: {
  items: SourceRow[];
  open: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  onEdit: (src: SourceRow) => void;
  onDelete: (src: SourceRow) => void;
  onSetDepartment: (src: SourceRow, department: string | null) => void;
  savingDept: Set<string>;
}) {
  if (items.length === 0) return null;
  const sections = groupByDepartment(items);
  return (
    <div className="rounded-xl border border-ink-100 bg-surface overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-ink-50 transition-colors"
      >
        <ChevronRight
          size={14}
          strokeWidth={2}
          className={cn('text-ink-500 transition-transform duration-200', open && 'rotate-90')}
        />
        <span className="text-ink-500">
          <FileText size={13} strokeWidth={1.75} />
        </span>
        <span className="flex-1 text-left text-[13px] font-bold text-ink-800">Interna dokument</span>
        <span className="text-[11px] font-bold text-ink-500 tabular-nums">{items.length}</span>
      </button>
      {open && (
        <div className="border-t border-ink-100">
          {sections.map((section) => (
            <div key={section.key}>
              <div className="flex items-center gap-2 px-4 py-2 bg-ink-50/60 border-b border-ink-100">
                <Building2 size={11} strokeWidth={2} className="text-ink-500" />
                <span className="flex-1 text-[10px] font-bold uppercase tracking-wider text-ink-500">
                  {section.dept}
                </span>
                <span className="text-[10px] font-bold text-ink-500 tabular-nums">{section.items.length}</span>
              </div>
              <ul className="divide-y divide-ink-100">
                {section.items.map((src) => (
                  <li key={src.filename}>
                    <div className="group/row flex items-center gap-3 px-4 py-2.5 hover:bg-ink-50 transition-colors">
                      <FileText size={12} strokeWidth={1.75} className="text-ink-500 shrink-0" />
                      <span
                        className="flex-1 min-w-0 text-[12.5px] font-semibold text-ink-700 truncate"
                        title={src.filename}
                      >
                        {displayName(src)}
                      </span>
                      {isAdmin && (
                        <DepartmentSelect
                          value={src.department ?? null}
                          saving={savingDept.has(src.filename)}
                          onChange={(dep) => onSetDepartment(src, dep)}
                        />
                      )}
                      <IndexedDate src={src} />
                      {src.chunk_count === 0 ? (
                        <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[10px] font-semibold uppercase tracking-[0.06em] text-amber-700">
                          <TriangleAlert size={10} strokeWidth={2} />
                          Tom
                        </span>
                      ) : (
                        <span className="shrink-0 text-[10px] font-semibold text-ink-500 tabular-nums">
                          {src.chunk_count} chunks
                        </span>
                      )}
                      {isAdmin && (
                        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover/row:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(src);
                            }}
                            className="p-1.5 rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 transition-colors"
                            title="Redigera innehåll"
                            aria-label={`Redigera ${src.filename}`}
                          >
                            <Pencil size={12} strokeWidth={1.75} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(src);
                            }}
                            className="p-1.5 rounded-md text-ink-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                            title="Ta bort källa"
                            aria-label={`Ta bort ${src.filename}`}
                          >
                            <Trash2 size={12} strokeWidth={1.75} />
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DepartmentSelect({
  value,
  saving,
  onChange,
}: {
  value: string | null;
  saving: boolean;
  onChange: (department: string | null) => void;
}) {
  // Visa ev. egen (icke-listad) avdelning som ett val så den inte tappas bort.
  const options =
    value && !DEPARTMENTS.includes(value as (typeof DEPARTMENTS)[number])
      ? [...DEPARTMENTS, value]
      : [...DEPARTMENTS];
  return (
    <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <select
        value={value ?? ''}
        disabled={saving}
        onChange={(e) => onChange(e.target.value || null)}
        aria-label="Avdelning"
        className={cn(
          'appearance-none cursor-pointer rounded-md border bg-surface pl-2 pr-6 py-1 text-[11px] font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-ink-100',
          value ? 'border-brand-200 text-brand-700' : 'border-ink-200 text-ink-500',
          saving && 'opacity-50',
        )}
      >
        <option value="">{UNCATEGORIZED}</option>
        {options.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
      <ChevronRight
        size={11}
        strokeWidth={2.5}
        className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 rotate-90 text-ink-500"
      />
    </div>
  );
}

function EmptySources({ onAddUrl, onAddFile }: { onAddUrl: () => void; onAddFile: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
      <Library size={36} strokeWidth={1.25} className="text-ink-500 mb-4" />
      <h3 className="text-display text-[17px] text-ink-900">Kunskapsbasen är tom</h3>
      <p className="text-[13px] text-ink-500 max-w-sm leading-relaxed mt-2">
        Lägg till en URL eller ladda upp en TXT-fil så börjar ELvis indexera den automatiskt.
      </p>
      <div className="flex gap-2 mt-6">
        <Button variant="secondary" size="sm" leftIcon={<Link2 size={14} strokeWidth={1.75} />} onClick={onAddUrl}>
          Lägg till URL
        </Button>
        <Button size="sm" leftIcon={<FileUp size={14} strokeWidth={1.75} />} onClick={onAddFile}>
          Ladda upp fil
        </Button>
      </div>
    </div>
  );
}

function InstructionsPanel() {
  return (
    <div className="space-y-5">
      <Card variant="glass" className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <HelpCircle size={16} strokeWidth={1.75} className="text-ink-500" />
          <div>
            <h3 className="font-bold text-ink-900 text-[14px] leading-none">Så lägger du till innehåll</h3>
            <p className="text-[11px] font-semibold text-ink-500 mt-1">Två vägar — båda tar några sekunder</p>
          </div>
        </div>

        <div className="space-y-4">
          <StepBlock
            number="1"
            icon={<Link2 size={14} strokeWidth={1.75} />}
            title="URL från webben"
            body="Klistra in adressen till en lagtext, riktlinje eller informationssida. Systemet hämtar sidan, strippar menyer/fotnoter och indexerar innehållet."
            tip="Funkar inte på JavaScript-tunga sidor (t.ex. gamla intranät där text laddas dynamiskt)."
          />
          <StepBlock
            number="2"
            icon={<FileUp size={14} strokeWidth={1.75} />}
            title="Ladda upp textfil"
            body="Dra och släpp en .txt-fil (max 1,5 MB) i uppladdningsrutan. Filen chunkas automatiskt och embedded semantiskt."
            tip="Har du PDF eller Word? Konvertera till text först — se nedan."
          />
        </div>
      </Card>

      <Card variant="glass" className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <FileType2 size={16} strokeWidth={1.75} className="text-ink-500" />
          <div>
            <h3 className="font-bold text-ink-900 text-[14px] leading-none">Konvertera till TXT</h3>
            <p className="text-[11px] font-semibold text-ink-500 mt-1">
              ELvis behöver ren text — här är enklaste vägen från vanliga format
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <ConvertRow
            format="PDF"
            steps="Öppna PDF:en → Markera all text (Ctrl+A) → Kopiera (Ctrl+C) → Klistra in i Anteckningar/Notepad → Spara som .txt"
          />
          <ConvertRow
            format="Word (.docx)"
            steps='I Word: Arkiv → Spara som → välj format "Oformaterad text (*.txt)" → Spara'
          />
          <ConvertRow
            format="Excel (.xlsx)"
            steps='Spara arket som "CSV UTF-8" eller "Text (tabavgränsad)" och döp om filändelsen till .txt'
          />
          <ConvertRow
            format="Webbsida"
            steps="Hoppa över — använd URL-importen ovan istället, den hämtar sidan åt dig"
          />
        </div>

        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
          <AlertCircle size={14} strokeWidth={2} className="text-amber-700 shrink-0 mt-0.5" />
          <p className="text-[11.5px] text-amber-900 leading-relaxed">
            Har ni PDF med tabeller eller bilder där texten inte går att markera? Skicka filen till oss
            (<a href="mailto:info@samify.se" className="font-bold underline">info@samify.se</a>) så
            extraherar vi texten åt er.
          </p>
        </div>
      </Card>

      <Card variant="glass" className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen size={16} strokeWidth={1.75} className="text-ink-500" />
          <div>
            <h3 className="font-bold text-ink-900 text-[14px] leading-none">Bra att veta</h3>
            <p className="text-[11px] font-semibold text-ink-500 mt-1">Tips för en välmående kunskapsbas</p>
          </div>
        </div>

        <ul className="space-y-2 text-[12.5px] text-ink-700 leading-relaxed">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-ink-400 mt-[7px] shrink-0" />
            <span>
              <strong className="text-ink-900">Uppdaterade dokument ersätter gamla.</strong> Ladda upp
              samma filnamn igen så byts innehållet ut automatiskt — inga dubbletter.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-ink-400 mt-[7px] shrink-0" />
            <span>
              <strong className="text-ink-900">Mer ≠ alltid bättre.</strong> ELvis presterar bäst på
              fokuserade dokument. Dela upp långa PDF:er i logiska delar om möjligt.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-ink-400 mt-[7px] shrink-0" />
            <span>
              <strong className="text-ink-900">Obesvarade frågor visar luckor.</strong> Gå till{' '}
              <em>Insikter</em> för att se vilka frågor ELvis missar — perfekt guide för vad som ska
              läggas till härnäst.
            </span>
          </li>
        </ul>
      </Card>
    </div>
  );
}

function StepBlock({
  number,
  icon,
  title,
  body,
  tip,
}: {
  number: string;
  icon: React.ReactNode;
  title: string;
  body: string;
  tip?: string;
}) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-ink-900 text-surface inline-flex items-center justify-center font-display text-sm">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1 text-ink-700">
          {icon}
          <p className="text-[13px] font-bold text-ink-900">{title}</p>
        </div>
        <p className="text-[12.5px] text-ink-600 leading-relaxed">{body}</p>
        {tip && <p className="text-[11.5px] text-ink-500 mt-1 italic">Obs: {tip}</p>}
      </div>
    </div>
  );
}

function ConvertRow({ format, steps }: { format: string; steps: string }) {
  return (
    <div className="flex gap-3 p-3 rounded-xl bg-surface border border-ink-100">
      <span className="shrink-0 inline-flex items-center justify-center px-2 py-0.5 rounded-md bg-ink-100 text-ink-700 text-[10px] font-semibold uppercase tracking-wider h-fit">
        {format}
      </span>
      <p className="text-[12px] text-ink-700 leading-relaxed">{steps}</p>
    </div>
  );
}
